import { BookmarkButton } from '@/components/shared/BookmarkButton';
import { NutritionBadgeDetails } from '@/components/shared/NutritionBadgeDetails';
import NutritionStatusCard from '@design-system/Cards/NutritionStatusCard';
import { NavBar } from '@design-system/NavBar';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { MyFoodDetailsSkeleton } from '@screens/MyFoodsDetailPage/MyFoodsDetailPageSkeleton';
import { MyFoodsDetailsFooterPage } from '@screens/MyFoodsDetailPage/MyFoodsDetailsFooterPage';
import { MyFoodsPageBasicDetails } from '@screens/MyFoodsDetailPage/MyFoodsPageBasicDetails';
import { MyFoodsPageCategoryDetails } from '@screens/MyFoodsDetailPage/MyFoodsPageCategoryDetails';
import { cn } from '@utils/styles';
import { BookmarkFlag } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import {
  useGetFoodDetailQuery,
  useGetFoodMetaDetailQuery,
  useSaveFoodMutation,
} from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import { ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

interface ShowMoreMetaData {
  description: string;
  links?: string[] | [];
  title: string;
}

interface CardDescriptionInfoProps {
  data?: ShowMoreMetaData;
  'data-testid'?: string;
}

const CardDescriptionInfo = ({ data, 'data-testid': testId }: CardDescriptionInfoProps) => {
  const [displayLinks, setDisplayLinks] = useState<boolean>(false);
  const { description, links } = data || {};

  const onClickSupportingResearch = () => {
    if (links && links.length > 0) {
      window.open(links[0], '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      data-testid={testId}
      className={cn(
        'mt-5 flex flex-col justify-between flex-1 h-full',
        !displayLinks && 'h-60',
      )}
    >
      <ScrollArea data-testid={`${testId}-scrollArea`}>
        <span 
          data-testid={`${testId}-description`}
          className="typography-body text-secondary"
        >
          {description}
        </span>
        <ScrollBar />
      </ScrollArea>

      {links && links.length > 0 && (
        <>
          {links.length > 1 ? (
            <div
              data-testid={`${testId}-researchSection`}
              className="mt-6 flex flex-col justify-between cursor-pointer"
              onClick={() => setDisplayLinks(!displayLinks)}
            >
              <div 
                data-testid={`${testId}-researchHeader`}
                className="flex justify-between items-end"
              >
                <span 
                  data-testid={`${testId}-researchTitle`}
                  className="typography-body text-primary"
                >
                  Supporting research
                </span>
                {displayLinks ? (
                  <ChevronUp 
                    data-testid={`${testId}-chevronUp`}
                    className="text-primary" 
                  />
                ) : (
                  <ChevronDown 
                    data-testid={`${testId}-chevronDown`}
                    className="text-primary" 
                  />
                )}
              </div>
              {displayLinks && (
                <div 
                  data-testid={`${testId}-linksList`}
                  className="flex gap-2 flex-col mt-3"
                >
                  {links.map((link, index) => (
                    <div 
                      key={`${index}-links`} 
                      data-testid={`${testId}-linkItem-${index}`}
                      className="truncate my-3"
                    >
                      <div className="flex items-center justify-between">
                        <a
                          data-testid={`${testId}-linkUrl-${index}`}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="typography-body text-primary truncate"
                        >
                          {link}
                        </a>
                        <ChevronRight 
                          data-testid={`${testId}-linkChevron-${index}`}
                          color="#a0a1ad" 
                        />
                      </div>
                      <hr className="border-stroke-primary mt-1" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div
              data-testid={`${testId}-singleResearchLink`}
              className="mt-6 flex cursor-pointer justify-between"
              onClick={onClickSupportingResearch}
            >
              <span 
                data-testid={`${testId}-singleResearchTitle`}
                className="typography-btn-large text-primary"
              >
                Supporting research
              </span>
              <ChevronRight 
                data-testid={`${testId}-singleResearchChevron`}
                className="text-primary" 
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const NutritionFoodDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [bookmarkStatus, setBookmarkStatus] = useState<boolean>(
    location?.state?.isBookmarked,
  );

  const param = {
    food_id: Number(id),
  };

  const {
    data,
    isLoading: isFoodDetailsAPILoading,
    isError: isFoodDetailsAPIError,
  } = useGetFoodDetailQuery(param);

  const {
    about: aboutFood = {
      about_this_food: '',
      macronutrients: [],
      food_composition: '',
      how_to_consume: '',
    },
    badges = [],
    whyThisFood: categoryDetails = [],
    basic_details: basicDetails = {
      color: '',
      image_url: '',
      name: '',
      rank: '',
      servings: '',
    },
  } = data || {};

  const { data: showMoreData } = useGetFoodMetaDetailQuery(param);

  const { meta_data: showMoreMetaData = [] } = showMoreData || {};

  const handleOnSaveMutationSuccess = () => {
    setBookmarkStatus((previousValue) => !previousValue);
  };

  const { mutate, isPending } = useSaveFoodMutation({
    onSuccess: handleOnSaveMutationSuccess,
  });

  const handleOnBookmark = () => {
    const bookmarkFlag = bookmarkStatus
      ? BookmarkFlag.Remove
      : BookmarkFlag.Add;

    mutate({
      food_id: Number(id),
      flag: bookmarkFlag,
    });
  };

  if (isFoodDetailsAPILoading) {
    return <MyFoodDetailsSkeleton data-testid="foodDetail-loading" />;
  }

  if (isFoodDetailsAPIError) {
    return <ErrorLoadingPage data-testid="foodDetail-error" variant="light" />;
  }

  return (
    <section data-testid="foodDetail-page">
      <div 
        data-testid="foodDetail-container"
        className="grid mx-auto gap-6 container p-4 md:px-8 lg:px-24 lg:py-6 max-w-[960px]"
      >
        <NavBar data-testid="foodDetail-navbar" className="md:-ml-5">
          <BookmarkButton
            data-testid="foodDetail-bookmarkButton"
            bookmarked={bookmarkStatus}
            disabled={isPending}
            isPending={isPending}
            onClick={handleOnBookmark}
          />
        </NavBar>
        <div data-testid="foodDetail-basicDetailsSection">
          <MyFoodsPageBasicDetails 
            data-testid="foodDetail-basicDetails"
            basicDetailsInfo={basicDetails} 
          />
          {badges.length > 0 && (
            <div 
              data-testid="foodDetail-badgesSection"
              className="flex justify-center md:justify-start"
            >
              <NutritionBadgeDetails 
                data-testid="foodDetail-badges"
                badgeInfo={badges} 
              />
            </div>
          )}
        </div>
        {categoryDetails?.length > 0 && (
          <>
            <div 
              data-testid="foodDetail-whyThisFoodSection"
              className="pt-6"
            >
              <p 
                data-testid="foodDetail-whyThisFoodTitle"
                className="typography-h1"
              >
                Why it's a {basicDetails?.rank} for me
              </p>
              <p 
                data-testid="foodDetail-whyThisFoodDescription"
                className="typography-btn-large pt-4"
              >
                Here are the health systems where my body needs support
              </p>
            </div>
            <MyFoodsPageCategoryDetails 
              data-testid="foodDetail-categoryDetails"
              categoryDetailsInfo={categoryDetails} 
            />
          </>
        )}
        {showMoreMetaData?.length > 0 && (
          <div 
            data-testid="foodDetail-metaDataSection"
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch"
          >
            {showMoreMetaData?.map((data: ShowMoreMetaData, index: number) => (
              <div 
                key={data?.title} 
                data-testid={`foodDetail-metaDataCard-${index}`}
                className="pt-5"
              >
                <NutritionStatusCard
                  data-testid={`foodDetail-statusCard-${index}`}
                  categoryTitle={data?.title}
                  cardDescription={
                    <CardDescriptionInfo 
                      data-testid={`foodDetail-cardDescription-${index}`}
                      data={data} 
                    />
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {aboutFood && (
        <MyFoodsDetailsFooterPage 
          data-testid="foodDetail-footer"
          aboutFoodInfo={aboutFood} 
        />
      )}
    </section>
  );
};
